import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';

const CATEGORY_COLORS = ['#00A8FF', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#FF66C3', '#4bc0c8', '#ff9f40'];

const formatPercentage = (percentage) => {
    if (typeof percentage === 'number' && !isNaN(percentage)) {
        return `${percentage.toFixed(2)}%`;
    }
    return '0.00%';
};

const buildChartOptions = (labels, total, onSliceSelect) => ({
    chart: {
        type: 'donut',
        width: '100%',
        animations: {
            enabled: true,
            animateGradually: { enabled: false },
        },
        events: {
            dataPointSelection: (_event, _chartContext, config) => {
                const idx = config?.dataPointIndex;
                const label = typeof idx === 'number' && labels?.[idx] ? labels[idx] : null;
                if (label && label !== 'No Data') {
                    onSliceSelect(label);
                }
            },
        },
    },
    labels: labels.length > 0 ? labels : ['No Data'],
    colors: CATEGORY_COLORS,
    plotOptions: {
        pie: {
            donut: {
                size: '65%',
                labels: {
                    show: true,
                    total: {
                        show: true,
                        showAlways: true,
                        label: 'Total Alerts',
                        fontSize: '14px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontWeight: 600,
                        color: '#373d3f',
                        formatter: () => total?.toString() || '0',
                    },
                },
            },
        },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    responsive: [{
        breakpoint: 100,
        options: { chart: { width: 70 }, legend: { show: false } },
    }],
});

const Category = ({ userData, analyticsData, loading, activeCategory, onCategorySelect }) => {
    const [localSelectedCategory, setLocalSelectedCategory] = useState(null);
    const selectedCategory = activeCategory !== undefined ? activeCategory : localSelectedCategory;

    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const lastSelectedIdxRef = useRef(null);
    const isMounted = useRef(true);
    const isUpdatingSelection = useRef(false);
    const onSliceSelectRef = useRef(() => {});

    const chartSeries = useMemo(
        () => analyticsData?.alerts_by_categories?.map((item) => item.count) || [],
        [analyticsData?.alerts_by_categories]
    );

    const chartLabels = useMemo(
        () => analyticsData?.alerts_by_categories?.map((item) => item.category) || [],
        [analyticsData?.alerts_by_categories]
    );

    const totalAlerts = analyticsData?.total_alerts || 0;

    const setSelected = useCallback((nextValue) => {
        if (!isMounted.current) return;
        onCategorySelect?.(nextValue);
        if (activeCategory === undefined) {
            setLocalSelectedCategory(nextValue);
        }
    }, [activeCategory, onCategorySelect]);

    const handleCategoryClick = useCallback((categoryName) => {
        if (!isMounted.current || isUpdatingSelection.current) return;
        const next = selectedCategory === categoryName ? null : categoryName;
        setSelected(next);
    }, [selectedCategory, setSelected]);

    useEffect(() => {
        onSliceSelectRef.current = handleCategoryClick;
    }, [handleCategoryClick]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (chartInstance.current) {
                try {
                    chartInstance.current.destroy();
                } catch (_) {}
                chartInstance.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (chartSeries.length > 0 || !chartInstance.current) return;
        try {
            chartInstance.current.destroy();
        } catch (_) {}
        chartInstance.current = null;
        lastSelectedIdxRef.current = null;
    }, [chartSeries.length]);

    // Single chart lifecycle: create once, then update in place (avoids double render blink)
    useEffect(() => {
        if (!isMounted.current || loading || chartSeries.length === 0 || !chartRef.current) {
            return undefined;
        }

        let cancelled = false;

        const syncChart = async () => {
            try {
                if (chartInstance.current) {
                    await chartInstance.current.updateSeries(chartSeries);
                    if (cancelled || !isMounted.current) return;

                    await chartInstance.current.updateOptions({
                        labels: chartLabels.length > 0 ? chartLabels : ['No Data'],
                        plotOptions: {
                            pie: {
                                donut: {
                                    labels: {
                                        total: {
                                            formatter: () => totalAlerts.toString(),
                                        },
                                    },
                                },
                            },
                        },
                    });
                    return;
                }

                const chart = new ApexCharts(
                    chartRef.current,
                    {
                        ...buildChartOptions(chartLabels, totalAlerts, (label) => onSliceSelectRef.current(label)),
                        series: chartSeries,
                    }
                );
                await chart.render();

                if (!cancelled && isMounted.current) {
                    chartInstance.current = chart;
                    lastSelectedIdxRef.current = null;
                } else {
                    await chart.destroy();
                }
            } catch (error) {
                console.warn('Category chart error:', error);
            }
        };

        syncChart();

        return () => {
            cancelled = true;
        };
    }, [chartSeries, chartLabels, totalAlerts, loading]);

    // Sync selected category highlight with chart (does not recreate chart)
    useEffect(() => {
        if (!chartInstance.current || !chartLabels?.length || loading || !isMounted.current) return;

        const syncSelection = () => {
            try {
                isUpdatingSelection.current = true;

                if (!selectedCategory) {
                    if (lastSelectedIdxRef.current !== null && chartInstance.current.clearSelection) {
                        chartInstance.current.clearSelection();
                        lastSelectedIdxRef.current = null;
                    }
                    return;
                }

                const nextIdx = chartLabels.indexOf(selectedCategory);
                if (nextIdx >= 0 && lastSelectedIdxRef.current !== nextIdx) {
                    if (chartInstance.current.clearSelection) {
                        chartInstance.current.clearSelection();
                    }
                    if (chartInstance.current.toggleDataPointSelection) {
                        chartInstance.current.toggleDataPointSelection(0, nextIdx);
                    }
                    lastSelectedIdxRef.current = nextIdx;
                }
            } catch (error) {
                console.warn('Selection sync error:', error);
            } finally {
                requestAnimationFrame(() => {
                    if (isMounted.current) {
                        isUpdatingSelection.current = false;
                    }
                });
            }
        };

        syncSelection();
    }, [chartLabels, selectedCategory, loading]);

    return (
        <div className="row g-5">
            <div className="col-md-6 col-12">
                <div id="chart" className="w-100 d-flex justify-content-center">
                    <div style={{ width: '100%', maxWidth: 420, minHeight: 330 }}>
                        {chartSeries.length > 0 ? (
                            <div ref={chartRef} style={{ minHeight: 330 }} />
                        ) : (
                            <div
                                className="w-100 d-flex align-items-center justify-content-center text-gray-500"
                                style={{ height: 330 }}
                            >
                                {loading ? 'Loading categories...' : 'No category data available.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="col-md-5 mx-auto col-12">
                <p className="fs-4 fw-bold mb-7">Alerts by categories</p>
                <div className="d-flex flex-column content-justify-center w-100">
                    {analyticsData?.alerts_by_categories?.map((item, index) => {
                        const isSelected = item.category === selectedCategory;
                        return (
                            <div
                                key={item.category}
                                className={`d-flex fs-6 fw-bold align-items-center mb-4 p-3 border rounded ${isSelected ? 'bg-light-danger border-danger' : ''}`}
                                onClick={() => handleCategoryClick(item.category)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div
                                    className="bullet w-8px h-8px rounded-2 me-3"
                                    style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                                />
                                <div className={`flex-grow-1 me-4 ${isSelected ? 'text-danger fw-bolder' : 'text-gray-800'}`}>
                                    {item.category}
                                </div>
                                <div className="fw-semibold fs-6 text-gray-700 text-xxl-end me-4">
                                    {formatPercentage(item.percentage)}
                                </div>
                                <div className="fw-bolder fs-5 text-gray-800 text-xxl-end min-w-100px">
                                    {item.count}
                                </div>
                            </div>
                        );
                    })}
                    {(!analyticsData?.alerts_by_categories || analyticsData.alerts_by_categories.length === 0) && (
                        <div className="text-gray-500">No categories found.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Category;
